import { css } from '@emotion/react'
import { Alert, HFlow, Theme, useTheme, VFlow } from 'bold-ui'
import { useErrorHandler } from 'components/error'
import { PageContainer } from 'components/layout/PageContainer'
import { useFlags } from 'config/useFlagsContext'
import { useAcessoConectesusRndsMutation } from 'graphql/hooks.generated'
import { TipoAtendimentoProfissional } from 'graphql/types.generated'
import { SdcPopperButton } from 'hooks/sdc/components/SdcPopperButton'
import { SdcToastList } from 'hooks/sdc/components/SdcToastList'
import { useCabecalhoFixo } from 'hooks/useCabecalhoFixo'
import { forwardRef, Fragment, Ref, useRef } from 'react'
import { useHistory, useLocation, useRouteMatch } from 'react-router'
import { formatDateAndHoursMinutes } from 'util/date/formatDate'
import { tipoAtendimentoRetificacaoRecord } from 'view/retificacao-atendimento/model-retificacaoAtendimento'

import { ButtonAcessoRnds } from '../ButtonAcessoRnds'
import { AtendimentoHeaderCidadaoSection } from './AtendimentoHeaderCidadaoSection'
import { AtendimentoHeaderTabs } from './AtendimentoHeaderTabs'
import { useAtendimentoHeaderConfig } from './useAtendimentoHeaderConfig'

export interface AtendimentoHeaderProps {
  cidadaoId: ID
  cnsCidadao?: string
  cnsProfissional?: string
  cnes?: string
  municipioId?: ID
  renderFolhaRosto?: boolean
  renderSoap?: boolean
  renderVacinacao?: boolean
  renderHistorico?: boolean
  renderCadastroCidadao?: boolean
  renderEscutaInicial?: boolean
  renderDiscussaoCaso?: boolean
  renderAgendamentos?: boolean
  renderSistemasExternos?: boolean
  renderAcompanhamentos?: boolean
  renderProntuarioFamiliar?: boolean
  labelEscutaPreAtendimento?: string
  dataAtendimento?: Instant
  showAlertasSdc?: boolean
  tipoAtendimentoProfissional?: TipoAtendimentoProfissional
  isRetificacao?: boolean
}

export const AtendimentoHeader = forwardRef((props: AtendimentoHeaderProps, ref: Ref<HTMLElement>) => {
  const {
    cidadaoId,
    labelEscutaPreAtendimento,
    cnes,
    cnsProfissional,
    cnsCidadao,
    municipioId,
    dataAtendimento,
    renderFolhaRosto = true,
    renderSoap = true,
    renderVacinacao = true,
    renderHistorico = true,
    renderCadastroCidadao = true,
    renderAgendamentos = true,
    renderEscutaInicial = false,
    renderDiscussaoCaso = false,
    renderAcompanhamentos = false,
    showAlertasSdc = false,
    tipoAtendimentoProfissional,
    isRetificacao = false,
    renderProntuarioFamiliar = false,
  } = props
  const { SISTEMAS_EXTERNOS_RNDS_ENABLED, PILOTO_PRONTUARIO_FAMILIAR_ENABLED } = useFlags()

  const theme = useTheme()
  const isFixo = useCabecalhoFixo()
  const classes = createStyles(theme, isFixo)
  const sdcButtonRef = useRef<HTMLDivElement>()
  const [gerarLinkAcessoMutation] = useAcessoConectesusRndsMutation()
  const history = useHistory()
  const match = useRouteMatch()
  const location = useLocation()

  const { hasIframesAtendimento, configRnds } = useAtendimentoHeaderConfig(cidadaoId, tipoAtendimentoProfissional)

  const isConexaoAtiva = configRnds?.conexao?.internet?.habilitado
  const isRndsAtiva = !!configRnds?.configuracaoCertificadoHabilitado?.rndsHabilitado
  const shouldShowButtonAcessoRnds = !isRetificacao && isConexaoAtiva && isRndsAtiva
  const hasSistemaExternoRnds = SISTEMAS_EXTERNOS_RNDS_ENABLED && shouldShowButtonAcessoRnds
  const shouldRenderSistemasExternos = hasIframesAtendimento || hasSistemaExternoRnds

  const handleRndsRejection = useErrorHandler()
  const handleRndsClick = SISTEMAS_EXTERNOS_RNDS_ENABLED
    ? () => {
        history.push(`${match.url}/sistemas-externos/rnds`, {
          from: location.pathname,
        })
      }
    : () => {
        gerarLinkAcessoMutation({
          variables: {
            input: {
              cnes: cnes,
              cnsCidadao: cnsCidadao,
              cnsProfissional: cnsProfissional,
              municipio: municipioId,
            },
          },
        })
          .then((result) => {
            window.open(result.data.acessoConecteSus, '_blank')
          })
          .catch(handleRndsRejection)
      }

  const descricaoTipoAtendimento =
    (tipoAtendimentoProfissional && tipoAtendimentoRetificacaoRecord[tipoAtendimentoProfissional]?.title) ??
    'atendimento'

  return (
    <header ref={ref} css={classes.container}>
      <PageContainer fluid>
        <VFlow vSpacing={0.25}>
          <HFlow alignItems='flex-start' hSpacing={0.25} justifyContent='space-between' style={classes.header}>
            <AtendimentoHeaderCidadaoSection
              cidadaoId={cidadaoId}
              dataAtendimento={dataAtendimento}
              showTelefoneAndUnidade={renderDiscussaoCaso}
            />
            {shouldShowButtonAcessoRnds && <ButtonAcessoRnds disabled={!cnsCidadao} onClick={handleRndsClick} />}
          </HFlow>
          {!isRetificacao ? (
            <HFlow style={classes.atendimentoHeader}>
              <AtendimentoHeaderTabs
                cidadaoId={cidadaoId}
                labelEscutaPreAtendimento={labelEscutaPreAtendimento}
                renderAgendamentos={renderAgendamentos}
                renderCadastroCidadao={renderCadastroCidadao}
                renderEscutaInicial={renderEscutaInicial}
                renderFolhaRosto={renderFolhaRosto}
                renderHistorico={renderHistorico}
                renderSoap={renderSoap}
                renderVacinacao={renderVacinacao}
                renderDiscussaoCaso={renderDiscussaoCaso}
                renderSistemasExternos={shouldRenderSistemasExternos}
                renderAcompanhamentos={renderAcompanhamentos}
                renderProntuarioFamiliar={PILOTO_PRONTUARIO_FAMILIAR_ENABLED && renderProntuarioFamiliar}
              />
              {showAlertasSdc && (
                <Fragment>
                  <SdcPopperButton ref={sdcButtonRef} />
                  <SdcToastList referenceElement={sdcButtonRef} />
                </Fragment>
              )}
            </HFlow>
          ) : (
            <Alert styles={{ container: classes.alertRetificacao }} type='info'>
              Você está editando o {descricaoTipoAtendimento} de {formatDateAndHoursMinutes(dataAtendimento, '-')}
            </Alert>
          )}
        </VFlow>
      </PageContainer>
    </header>
  )
})

const createStyles = (theme: Theme, isFixo: boolean) => ({
  container: css`
    border-bottom: 1px solid ${theme.pallete.divider};
    background-color: ${theme.pallete.surface.main};
    position: sticky;
    top: 0;
    z-index: 9;
    ${isFixo ? `box-shadow: 0 1px 5px 0 ${theme.pallete.divider}, 0 2px 1px -1px ${theme.pallete.divider};` : ''}
  `,
  header: css`
    margin-top: 1rem;
    gap: 2rem;
  `,
  atendimentoHeader: css`
    grid-template-columns: 1fr auto;
  `,
  alertRetificacao: css`
    width: 100%;
  `,
  buttonSdc: css`
    margin-bottom: 0.5rem;
  `,
})
